import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, Loader2, CheckCircle, Plus, Briefcase } from 'lucide-react';
import { DatePicker, TimePicker } from '../DateTimePicker';
import { getWorkersDropdown } from '../../services/api';

export function CreateJobModal({ isOpen, onClose, onSubmit, onAddToJob, loading, descriptions = [], existingJobs = [] }) {
  const [mode, setMode] = useState('new'); // 'new' | 'existing'
  const [selectedJobId, setSelectedJobId] = useState('');
  const [formData, setFormData] = useState({
    date_start: '', date_end: '', start_time: '', end_time: '',
    assignee_id: '', remarks: '', selectedDescIds: [], is_to_book: false
  });

  const { data: workers } = useQuery({
    queryKey: ['workersDropdown'],
    queryFn: () => getWorkersDropdown().then(res => res.data),
    enabled: isOpen
  });

  // Only show unbooked descriptions
  const unbookedDescs = descriptions.filter(d => !d.is_assigned);

  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({ ...prev, selectedDescIds: unbookedDescs.map(d => d.id) }));
      setMode(existingJobs.length > 0 ? 'existing' : 'new');
      setSelectedJobId(existingJobs.length > 0 ? existingJobs[0].id : '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const toggleDesc = (id) => {
    setFormData(prev => ({
      ...prev,
      selectedDescIds: prev.selectedDescIds.includes(id)
        ? prev.selectedDescIds.filter(x => x !== id)
        : [...prev.selectedDescIds, id]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.selectedDescIds.length === 0) { toast.error('Please select at least one description'); return; }

    if (mode === 'existing' && selectedJobId) {
      onAddToJob({
        job_id: selectedJobId,
        quote_description_ids: formData.selectedDescIds,
        date_start: formData.date_start || null,
        date_end: formData.date_end || null,
        assignees: formData.assignee_id ? [{
          assignee_id: formData.assignee_id,
          start_time: formData.start_time || '08:00',
          end_time: formData.end_time || '17:00'
        }] : [],
        remarks: formData.remarks,
      });
    } else {
      if (!formData.is_to_book) {
        if (!formData.date_start) { toast.error('Please select a start date'); return; }
        if (!formData.date_end) { toast.error('Please select an end date'); return; }
      }
      onSubmit(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-job-modal">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="font-semibold text-lg">{mode === 'existing' ? 'Add to Existing Job' : 'Create New Job'}</h2>
          <button onClick={onClose} data-testid="create-job-close"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">

          {/* Mode selection when existing jobs are available */}
          {existingJobs.length > 0 && (
            <div className="border rounded-lg p-3 bg-slate-50 space-y-2">
              <label className="block text-sm font-medium mb-1">What would you like to do?</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="jobMode" value="existing" checked={mode === 'existing'} onChange={() => setMode('existing')} />
                  <span>Add to existing job</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="jobMode" value="new" checked={mode === 'new'} onChange={() => setMode('new')} />
                  <span>Create new job</span>
                </label>
              </div>
              {mode === 'existing' && (
                <select value={selectedJobId} onChange={e => setSelectedJobId(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded text-sm mt-2" data-testid="existing-job-select">
                  {existingJobs.map(j => <option key={j.id} value={j.id}>{j.job_number}</option>)}
                </select>
              )}
            </div>
          )}

          {/* Select descriptions */}
          <div>
            <label className="block text-sm font-medium mb-2">Select Descriptions to Include</label>
            <div className="space-y-2 border rounded p-3 bg-slate-50">
              {unbookedDescs.length > 0 ? unbookedDescs.map(d => (
                <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={formData.selectedDescIds.includes(d.id)} onChange={() => toggleDesc(d.id)} className="rounded" />
                  <span>{d.job_title}</span>
                </label>
              )) : (
                <p className="text-sm text-slate-500">All descriptions are already booked into jobs.</p>
              )}
              {descriptions.filter(d => d.is_assigned).length > 0 && (
                <div className="mt-2 pt-2 border-t">
                  <p className="text-xs text-slate-400 mb-1">Already booked:</p>
                  {descriptions.filter(d => d.is_assigned).map(d => (
                    <div key={d.id} className="flex items-center gap-2 text-sm text-slate-400">
                      <input type="checkbox" checked disabled className="rounded opacity-40" />
                      <span className="line-through">{d.job_title}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* To Book (No Schedule) — only for new jobs */}
          {mode === 'new' && (
            <div>
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="job-to-book-label">
                <input
                  type="checkbox"
                  checked={formData.is_to_book}
                  onChange={(e) => setFormData({ ...formData, is_to_book: e.target.checked })}
                  className="rounded"
                  data-testid="job-to-book-checkbox"
                />
                <span>To book (no schedule)</span>
              </label>
            </div>
          )}

          {/* Dates and Times (hidden for new jobs marked as To Book) */}
          {!(mode === 'new' && formData.is_to_book) && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Start Date {mode === 'new' && <span className="text-red-500">*</span>}</label>
                  <DatePicker value={formData.date_start} onChange={(v) => setFormData({...formData, date_start: v})} testId="job-start-date" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End Date {mode === 'new' && <span className="text-red-500">*</span>}</label>
                  <DatePicker value={formData.date_end} onChange={(v) => setFormData({...formData, date_end: v})} testId="job-end-date" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Start Time</label>
                  <TimePicker value={formData.start_time} onChange={(v) => setFormData({...formData, start_time: v})} testId="job-start-time" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End Time</label>
                  <TimePicker value={formData.end_time} onChange={(v) => setFormData({...formData, end_time: v})} testId="job-end-time" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Assigned Person</label>
                <select value={formData.assignee_id} onChange={(e) => setFormData({...formData, assignee_id: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" data-testid="job-assignee">
                  <option value="">Select Person</option>
                  {workers?.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
            </>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Remarks</label>
            <textarea value={formData.remarks} onChange={(e) => setFormData({...formData, remarks: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" rows={3} placeholder="Enter any remarks..." data-testid="job-remarks" />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={loading || unbookedDescs.length === 0} className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary-hover flex items-center gap-2" data-testid="create-job-submit">
              {loading && <Loader2 size={16} className="animate-spin" />}
              {mode === 'existing' ? 'Add to Job' : 'Create Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Revise Quote Modal

